import * as Lucide from "lucide-react";
import { ArrowRight } from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import { PageHero, SectionLabel, GhostNumber, CTA, EditorialMarquee } from "@/components/site/Bits";
import { Reveal, RevealStagger, RevealItem } from "@/components/site/Reveal";
import { Counter } from "@/components/site/Counter";
import {
  CHALLENGES, BENEFITS, B2B_MANIFESTO, PARTNERSHIP_STEPS,
  REVENUE_STATS, EXPECTATIONS, WHY_NOW, IMAGES,
} from "@/data/content";

function Icon({ name, className }) {
  const C = Lucide[name] || Lucide.Circle;
  return <C className={className} />;
}

export default function ForConsultancies() {
  useSeo(
    "For Job Consultancies — Build a profitable partnership | UK Student Job",
    "Partner with UK Student Job: an abundant, pre-verified international talent pool across Southampton and London for all role types. No sourcing cost, extremely low compliance risk, revenue from day one."
  );

  return (
    <>
      <PageHero
        testId="consultancies-hero"
        label="For Job Consultancies"
        lines={[<>Build a profitable</>, <span key="r" className="text-[#C8102E]">partnership</span>, <>— without limits</>]}
        sub="An abundant, pre-verified international talent pool across Southampton and London. Available for part-time, seasonal, full-time, fixed-term, and permanent roles. No sourcing cost. Extremely low compliance risk. Revenue from day one."
      >
        <CTA to="/contact" testId="consult-hero-primary">
          Become a partner today <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </CTA>
        <CTA href="#how-it-works" variant="secondary" testId="consult-hero-secondary">
          See how it works
        </CTA>
      </PageHero>

      {/* Challenge */}
      <section className="bg-[#F4F6F9] py-24 lg:py-32" data-testid="consult-challenge">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal><SectionLabel>The challenge</SectionLabel></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-outfit font-extrabold text-[#0A1F44] text-3xl lg:text-5xl tracking-tight max-w-3xl">
              The three friction points every consultancy faces
            </h2>
          </Reveal>
          <RevealStagger className="mt-16 grid md:grid-cols-3 gap-6">
            {CHALLENGES.map((c, i) => (
              <RevealItem key={c.title}>
                <div className="relative h-full bg-white rounded-2xl p-9 border border-[#0A1F44]/8 shadow-[0_8px_32px_rgba(10,31,68,0.05)] hover:-translate-y-2 transition-transform duration-500">
                  <GhostNumber className="absolute top-3 right-5 text-7xl">{`0${i + 1}`}</GhostNumber>
                  <div className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-[#C8102E]/10 text-[#C8102E]">
                    <Icon name={c.icon} className="h-7 w-7" />
                  </div>
                  <h3 className="mt-6 font-outfit font-bold text-xl text-[#0A1F44]">{c.title}</h3>
                  <p className="mt-3 text-[#12294D]/70 text-sm leading-relaxed font-manrope">{c.body}</p>
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-white py-24 lg:py-32" data-testid="consult-benefits">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-3xl">
            <Reveal><SectionLabel>Partnership benefits</SectionLabel></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-outfit font-extrabold text-[#0A1F44] text-3xl lg:text-5xl tracking-tight">
                Your six partnership benefits
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-5 text-[#12294D]/75 text-lg font-manrope">
                One partnership agreement. Six immediate advantages for your consultancy business.
              </p>
            </Reveal>
          </div>
          <RevealStagger className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6" stagger={0.07}>
            {BENEFITS.map((b) => (
              <RevealItem key={b.title}>
                <div className="group h-full bg-[#F4F6F9] rounded-2xl p-8 border border-[#0A1F44]/8 hover:bg-[#0A1F44] transition-colors duration-500">
                  <div className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-[#0A1F44] text-white group-hover:bg-[#C8102E] transition-colors duration-300">
                    <Icon name={b.icon} className="h-7 w-7" />
                  </div>
                  <h3 className="mt-6 font-outfit font-bold text-xl text-[#0A1F44] group-hover:text-white transition-colors duration-500">{b.title}</h3>
                  <p className="mt-3 text-[#12294D]/70 group-hover:text-white/70 text-sm leading-relaxed font-manrope transition-colors duration-500">{b.body}</p>
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* B2B-only manifesto */}
      <section className="relative bg-[#0A1F44] py-24 lg:py-32 overflow-hidden grain" data-testid="consult-b2b">
        <div className="absolute inset-0 flag-stripes opacity-25" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
          <div className="max-w-3xl">
            <Reveal><SectionLabel light>Our B2B-only commitment</SectionLabel></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-outfit font-extrabold text-white text-3xl lg:text-5xl tracking-tight">
                Why UK Student Job never works directly with employers
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-white/70 leading-relaxed font-manrope">
                This is a question we are frequently asked — and we believe it deserves a
                transparent, complete answer. UK Student Job operates exclusively through consultancy
                partners. We do not approach employers directly, establish direct commercial
                relationships with employers, or bypass our consultancy partners in any way. Here is why:
              </p>
            </Reveal>
          </div>
          <div className="mt-16 space-y-5">
            {B2B_MANIFESTO.map((m, i) => (
              <Reveal key={m.title} delay={i * 0.04}>
                <div className="group grid md:grid-cols-12 gap-6 items-start bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/[0.08] transition-colors duration-300">
                  <div className="md:col-span-2">
                    <GhostNumber light className="text-6xl lg:text-7xl">{`0${i + 1}`}</GhostNumber>
                  </div>
                  <div className="md:col-span-10">
                    <h3 className="font-outfit font-bold text-xl lg:text-2xl text-white">{m.title}</h3>
                    <p className="mt-3 text-white/70 leading-relaxed font-manrope">{m.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* How the partnership works */}
      <section id="how-it-works" className="bg-white py-24 lg:py-32 scroll-mt-24" data-testid="consult-how">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal><SectionLabel>How it works</SectionLabel></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-outfit font-extrabold text-[#0A1F44] text-3xl lg:text-5xl tracking-tight max-w-3xl">
              Three steps from agreement to active revenue
            </h2>
          </Reveal>
          <RevealStagger className="mt-16 grid md:grid-cols-3 gap-8">
            {PARTNERSHIP_STEPS.map((s, i) => (
              <RevealItem key={s.title}>
                <div className="relative h-full pt-8">
                  <div className="absolute top-0 left-0 text-[#C8102E] font-outfit font-extrabold text-lg">{`0${i + 1}`}</div>
                  <div className="h-[3px] w-full bg-[#0A1F44]/10 mb-6">
                    <div className="h-full bg-[#C8102E]" style={{ width: `${((i + 1) / 3) * 100}%` }} />
                  </div>
                  <h3 className="font-outfit font-bold text-xl text-[#0A1F44]">{s.title}</h3>
                  <p className="mt-3 text-[#12294D]/70 text-sm leading-relaxed font-manrope">{s.body}</p>
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* Revenue opportunity */}
      <section className="bg-[#F4F6F9] py-24 lg:py-32" data-testid="consult-revenue">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-6">
              <Reveal><SectionLabel>Revenue opportunity</SectionLabel></Reveal>
              <Reveal delay={0.05}>
                <h2 className="mt-5 font-outfit font-extrabold text-[#0A1F44] text-3xl lg:text-5xl leading-[1.05] tracking-tight">
                  One partnership. Recurring revenue month after month.
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 text-[#12294D]/75 leading-relaxed font-manrope">
                  Every candidate we refer and you successfully place generates income for your
                  consultancy at zero additional operational cost. We manage the compliance, the
                  onboarding guidance, the work-right briefings, and the ongoing HR liaison. You focus
                  on the employer relationships across Southampton and London. We deliver the compliant
                  international talent. Together we build a placement operation that neither party
                  could achieve alone.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <CTA to="/contact" testId="consult-revenue-cta">
                  Start your partnership <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </CTA>
              </Reveal>
            </div>
            <div className="lg:col-span-6">
              <RevealStagger className="grid grid-cols-2 gap-4">
                {REVENUE_STATS.map((s, i) => (
                  <RevealItem key={i} className={i === 4 ? "col-span-2" : ""}>
                    <div className="h-full bg-white rounded-2xl p-7 border border-[#0A1F44]/8 shadow-[0_8px_32px_rgba(10,31,68,0.05)]">
                      <div className="text-4xl font-outfit font-extrabold text-[#C8102E]">{s.text}</div>
                      <p className="mt-3 text-[#12294D]/70 text-sm leading-relaxed font-manrope">{s.label}</p>
                    </div>
                  </RevealItem>
                ))}
              </RevealStagger>
            </div>
          </div>
        </div>
      </section>

      <EditorialMarquee />

      {/* Expectations */}
      <section className="bg-white py-24 lg:py-32" data-testid="consult-expectations">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-3xl">
            <Reveal><SectionLabel>What we ask of our consultancy partners</SectionLabel></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-outfit font-extrabold text-[#0A1F44] text-3xl lg:text-5xl tracking-tight">
                Long-term, values-driven partnerships — built on mutual commitment
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-[#12294D]/75 leading-relaxed font-manrope">
                UK Student Job invests significantly in every consultancy partnership — through
                candidate supply, compliance infrastructure, and ongoing operational support. In
                return, we ask our partners to match that commitment. We are not looking for
                transactional arrangements. We are building long-term, professional partnerships that
                grow and strengthen over time.
              </p>
            </Reveal>
          </div>
          <RevealStagger className="mt-14 grid md:grid-cols-2 gap-6" stagger={0.06}>
            {EXPECTATIONS.map((e, i) => (
              <RevealItem key={e.title}>
                <div className="h-full bg-[#F4F6F9] rounded-2xl p-8 border-l-4 border-[#C8102E]">
                  <h3 className="font-outfit font-bold text-lg text-[#0A1F44]">{e.title}</h3>
                  <p className="mt-3 text-[#12294D]/70 text-sm leading-relaxed font-manrope">{e.body}</p>
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* Why act now */}
      <section className="bg-[#F4F6F9] py-24 lg:py-32" data-testid="consult-why-now">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-3xl">
            <Reveal><SectionLabel>Why act now</SectionLabel></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-outfit font-extrabold text-[#0A1F44] text-3xl lg:text-5xl tracking-tight">
                The market timing, the competitive gap, and the talent pool are all aligned today.
              </h2>
            </Reveal>
          </div>
          <RevealStagger className="mt-14 grid md:grid-cols-3 gap-6">
            {WHY_NOW.map((w, i) => (
              <RevealItem key={w.title}>
                <div className="relative h-full bg-white rounded-2xl p-9 border border-[#0A1F44]/8 shadow-[0_8px_32px_rgba(10,31,68,0.05)]">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#0A1F44] text-white font-outfit font-bold">{i + 1}</div>
                  <h3 className="mt-6 font-outfit font-bold text-lg text-[#0A1F44]">{w.title}</h3>
                  <p className="mt-3 text-[#12294D]/70 text-sm leading-relaxed font-manrope">{w.body}</p>
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* Page CTA */}
      <section className="bg-white py-24 lg:py-32" data-testid="consult-cta">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="relative rounded-[2rem] bg-[#0A1F44] overflow-hidden grain p-10 lg:p-16 text-center">
            <div className="absolute inset-0 flag-stripes opacity-25" />
            <div className="relative max-w-3xl mx-auto">
              <Reveal>
                <h2 className="font-outfit font-extrabold text-white text-3xl lg:text-5xl leading-[1.05] tracking-tight">
                  Let's build this partnership together
                </h2>
              </Reveal>
              <Reveal delay={0.05}>
                <p className="mt-6 text-white/75 leading-relaxed font-manrope">
                  The international talent pool is ready. The compliance infrastructure is live across
                  Southampton and London. The only thing missing is your consultancy.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="mt-9 flex flex-wrap gap-4 justify-center">
                  <CTA to="/contact" testId="consult-cta-primary">
                    Become a consultancy partner <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </CTA>
                  <CTA to="/compliance" variant="secondary-light" testId="consult-cta-secondary">
                    View our compliance approach
                  </CTA>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
